import { useEffect, useState } from 'react';
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Container, Button, TablePagination, IconButton, Tooltip,
  MenuItem, Select, FormControl, InputLabel, TextField, Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PublishIcon from '@mui/icons-material/Publish';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import LinkIcon from '@mui/icons-material/Link';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Menus from '../../collections/menus';
import Menu from '../../models/menu';

const statusConfig = {
  'DRAFT': { label: 'Nháp', color: 'default' },
  'ACTIVE': { label: 'Đang dùng', color: 'success' },
  'COMPLETED': { label: 'Đã hoàn thành', color: 'info' },
  'ARCHIVED': { label: 'Lưu trữ', color: 'warning' },
};

const ListMenus = ({ setPageTitle }) => {
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  useEffect(() => {
    setPageTitle("Danh sách Menu KH");
  }, [setPageTitle]);

  useEffect(() => {
    fetchMenus();
  }, [page, rowsPerPage, statusFilter, search]);

  const fetchMenus = async () => {
    try {
      setLoading(true);
      setError(null);
      const menusCollection = new Menus();
      const params = {
        page: page + 1,
        per_page: rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
        ...(search && { search }),
      };
      const result = await menusCollection.fetch(params);
      if (result) {
        setTotal(result.total);
        setMenus(menusCollection.items || []);
      }
    } catch (err) {
      setError('Có lỗi xảy ra khi tải dữ liệu');
      setMenus([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (id, e) => {
    e.stopPropagation();
    try {
      const menuModel = new Menu();
      const result = await menuModel.publishMenu(id);
      if (result.success) {
        navigator.clipboard.writeText(result.url);
        toast.success('Đã xuất và sao chép link');
        fetchMenus();
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      toast.error('Có lỗi xảy ra khi xuất menu');
    }
  };

  const handleCopyUrl = (url, e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(url);
    toast.success('Đã sao chép link');
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bạn có chắc chắn muốn xóa menu này?')) {
      try {
        const menuModel = new Menu();
        const response = await menuModel.deleteMenu(id);
        if (response.success) {
          toast.success(response.message);
          fetchMenus();
        } else {
          toast.error(response.message);
        }
      } catch (err) {
        toast.error('Có lỗi xảy ra khi xóa menu');
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('vi-VN');
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', gap: '16px', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 160 }} size="small">
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={statusFilter}
              label="Trạng thái"
              onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">Tất cả</MenuItem>
              <MenuItem value="DRAFT">Nháp</MenuItem>
              <MenuItem value="ACTIVE">Đang dùng</MenuItem>
              <MenuItem value="COMPLETED">Đã hoàn thành</MenuItem>
              <MenuItem value="ARCHIVED">Lưu trữ</MenuItem>
            </Select>
          </FormControl>
          <TextField
            size="small"
            placeholder="Tìm theo tên KH..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            sx={{ minWidth: 200 }}
          />
        </div>
        <Button
          variant="contained"
          color="primary"
          onClick={() => navigate('/menus/new')}
        >
          Tạo menu mới
        </Button>
      </div>

      <TableContainer component={Paper}>
        {loading ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Đang tải...</div>
        ) : error ? (
          <div style={{ padding: '20px', textAlign: 'center', color: 'red' }}>{error}</div>
        ) : menus.length === 0 ? (
          <div style={{ padding: '20px', textAlign: 'center' }}>Không có dữ liệu</div>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Khách hàng</TableCell>
                  <TableCell>Tiêu đề</TableCell>
                  <TableCell>Trạng thái</TableCell>
                  <TableCell align="right">Tổng KG</TableCell>
                  <TableCell align="right">Số lần giao</TableCell>
                  <TableCell>Ngày tạo</TableCell>
                  <TableCell align="right">Thao tác</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {menus.map((menu) => (
                  <TableRow
                    key={menu.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/menus/${menu.id}/edit`)}
                  >
                    <TableCell>{menu.customer_name}</TableCell>
                    <TableCell>{menu.title}</TableCell>
                    <TableCell>
                      <Chip
                        label={statusConfig[menu.status]?.label || menu.status}
                        color={statusConfig[menu.status]?.color || 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="right">{menu.total_weight} kg</TableCell>
                    <TableCell align="right">{menu.total_deliveries}</TableCell>
                    <TableCell>{formatDate(menu.created_at)}</TableCell>
                    <TableCell align="right" onClick={(e) => e.stopPropagation()}>
                      {menu.public_url && (
                        <Tooltip title="Sao chép link">
                          <IconButton
                            onClick={(e) => handleCopyUrl(menu.public_url, e)}
                            color="success"
                            size="small"
                          >
                            <LinkIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="Xuất & Chia sẻ">
                        <IconButton
                          onClick={(e) => handlePublish(menu.id, e)}
                          color="success"
                          size="small"
                        >
                          <PublishIcon />
                        </IconButton>
                      </Tooltip>
                      <IconButton
                        onClick={() => navigate(`/menus/${menu.id}/preview`)}
                        color="info"
                        size="small"
                        title="Xem trước"
                      >
                        <VisibilityIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => navigate(`/menus/${menu.id}/edit`)}
                        color="primary"
                        size="small"
                        title="Sửa"
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        onClick={() => handleDelete(menu.id)}
                        color="error"
                        size="small"
                        title="Xóa"
                      >
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 50]}
              component="div"
              count={total}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            />
          </>
        )}
      </TableContainer>
    </Container>
  );
};

export default ListMenus;
